<template>
    <div>
        <a-modal
            :visible="true"
            :width="900"
            :footer="null"
            :closable="false"
            :destroy-on-close="true"
            centered
            wrap-class-name="tpm-wrap"
            @cancel="$emit('close')"
        >
            <div class="tpm-modal">
                <div class="tpm-head">
                    <div>
                        <h3>Produtos digitados</h3>
                        <div class="tpm-sub">{{ providerName }}<template v-if="sellerName"> · {{ sellerName }}</template></div>
                    </div>
                    <a-tooltip title="Fechar">
                        <a-button class="tpm-x" @click="$emit('close')">
                            <span class="material-icons-outlined">close</span>
                        </a-button>
                    </a-tooltip>
                </div>

                <div class="tpm-tools">
                    <a-input v-model="search" class="tpm-search" placeholder="Buscar produto..." allow-clear>
                        <a-icon slot="prefix" type="search" />
                    </a-input>
                    <a-button class="tpm-btn-ghost tpm-add-product" @click="showAddProductPicker = true">
                        <span class="material-icons-outlined">add</span>
                        Adicionar novo produto
                    </a-button>
                </div>

                <cc-loader-full v-if="loading" />
                <div class="tpm-body" v-else>
                    <a-table
                        size="small"
                        :columns="columns"
                        :data-source="filteredProducts"
                        :pagination="false"
                        row-key="id"
                        :locale="{ emptyText: 'Nenhum produto digitado por esse vendedor.' }"
                    >
                        <template slot="nome" slot-scope="text, product">
                            <div class="tpm-pname">{{ product.name }}</div>
                            <div class="tpm-pean" v-if="product.ean || product.internal_code">
                                <template v-if="product.ean">EAN {{ product.ean }}</template>
                                <template v-if="product.ean && product.internal_code"> · </template>
                                <template v-if="product.internal_code">Cód. {{ product.internal_code }}</template>
                            </div>
                        </template>

                        <template slot="digitacao" slot-scope="text, product">
                            {{ product.offer_price | currency }}
                        </template>

                        <template slot="quantidade" slot-scope="text, product">
                            {{ product.requested_quantity || 0 }}
                        </template>

                        <template slot="status" slot-scope="text, product">
                            <span v-if="product.is_winner" class="tpm-tag tpm-tag-winner">Vencedor</span>
                            <span v-else class="tpm-tag">Não vencedor</span>
                        </template>

                        <template slot="acoes" slot-scope="text, product">
                            <a-button class="tpm-btn" @click="openDistribution(product)">
                                {{ product.is_winner ? "Distribuir por loja" : "Adicionar" }}
                            </a-button>
                        </template>
                    </a-table>
                </div>

                <div class="tpm-foot">
                    <a-button class="tpm-btn-ghost" @click="$emit('close')">Fechar</a-button>
                </div>
            </div>
        </a-modal>

        <StoreQuantityModal
            v-if="distributionModal.open"
            :winner="distributionModal.winner"
            :product="distributionModal.product"
            :quotation-id="quotationId"
            :mark-winner-on-save="distributionModal.markWinnerOnSave"
            @close="closeDistribution"
            @saved="onDistributionSaved"
            @price-updated="onPriceUpdated"
        />

        <AddProductPickerModal
            v-if="showAddProductPicker"
            :quotation-id="quotationId"
            :provider-name="providerName"
            :seller-name="sellerName"
            :exclude-ids="products.flatMap((p) => [p.id, p.main_product_id]).filter(Boolean)"
            @close="showAddProductPicker = false"
            @select="openDistributionForNewProduct"
        />
    </div>
</template>

<script>
import RequestService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import StoreQuantityModal from "../comparativo/_store-quantity.modal.vue";
import AddProductPickerModal from "./_add-product-picker.modal.vue";

export default {
    name: "TypedProductsModal",
    components: { StoreQuantityModal, AddProductPickerModal },
    props: {
        quotationId: [String, Number],
        providerId: [String, Number],
        providerName: String,
        sellerId: [String, Number],
        sellerName: String,
    },
    data() {
        return {
            v3Svc: new RequestService(),
            products: [],
            loading: true,
            search: "",
            distributionModal: {
                open: false,
                winner: null,
                product: null,
                markWinnerOnSave: false,
            },
            showAddProductPicker: false,
        };
    },
    computed: {
        columns() {
            return [
                { title: "Produto", key: "nome", scopedSlots: { customRender: "nome" } },
                { title: "Digitação", key: "digitacao", align: "right", scopedSlots: { customRender: "digitacao" } },
                { title: "Qtd. solicitada", key: "quantidade", align: "center", width: 110, scopedSlots: { customRender: "quantidade" } },
                { title: "Status", key: "status", align: "center", scopedSlots: { customRender: "status" } },
                { title: "Ações", key: "acoes", width: 160, scopedSlots: { customRender: "acoes" } },
            ];
        },
        filteredProducts() {
            let list = this.products;

            if (this.search) {
                const term = this.search.toLowerCase();
                list = list.filter((p) => (p.name || "").toLowerCase().includes(term));
            }

            return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
        },
    },
    created() {
        this.loadProducts();
    },
    methods: {
        loadProducts() {
            this.loading = true;

            this.v3Svc
                .full_general_request_report(this.quotationId, this.sellerId)
                .then((response) => response.data)
                .then((data) => {
                    const grouped = new Map();

                    (data || []).forEach((row) => {
                        const existing = grouped.get(row.id);

                        if (existing) {
                            existing.requested_quantity = (existing.requested_quantity || 0) + (row.requested_quantity || 0);
                            return;
                        }

                        grouped.set(row.id, { ...row, requested_quantity: row.requested_quantity || 0 });
                    });

                    this.products = Array.from(grouped.values());
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        parsePackagingMultiplier(packing) {
            const value = parseInt(String(packing || "").split(" ")[1], 10);
            return Number.isFinite(value) && value > 0 ? value : 1;
        },
        openDistribution(product) {
            const packagingMultiplier = this.parsePackagingMultiplier(product.packing);

            this.distributionModal = {
                open: true,
                winner: {
                    pro_id: product.id,
                    idVendedor: this.sellerId,
                    valor: product.offer_price,
                    nomeFornecedor: this.providerName,
                    nomeVendedor: this.sellerName,
                    embalagem: packagingMultiplier,
                },
                product: {
                    descricaoProduto: product.name,
                    flag_generico: product.is_generic,
                    qtd_embalagem: packagingMultiplier,
                    embalagem: product.packing,
                    ean: product.ean,
                    internal_code: product.internal_code,
                },
                markWinnerOnSave: !product.is_winner,
            };
        },
        openDistributionForNewProduct(product) {
            const packagingMultiplier = this.parsePackagingMultiplier(product.pro_embalagem);

            this.showAddProductPicker = false;

            this.$nextTick(() => {
                this.distributionModal = {
                    open: true,
                    winner: {
                        pro_id: product.pro_id,
                        idVendedor: this.sellerId,
                        valor: 0,
                        nomeFornecedor: this.providerName,
                        nomeVendedor: this.sellerName,
                        embalagem: packagingMultiplier,
                    },
                    product: {
                        descricaoProduto: product.pro_descricao,
                        flag_generico: product.pro_generico,
                        qtd_embalagem: packagingMultiplier,
                        embalagem: product.pro_embalagem,
                        ean: product.pro_ean,
                        internal_code: product.pro_codInterno,
                    },
                    markWinnerOnSave: true,
                };
            });
        },
        closeDistribution() {
            this.distributionModal.open = false;
        },
        onDistributionSaved() {
            this.distributionModal.open = false;
            this.loadProducts();
            this.$emit("reload");
        },
        onPriceUpdated() {
            this.loadProducts();
            this.$emit("reload");
        },
    },
};
</script>

<style lang="scss" scoped>
.tpm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.tpm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.tpm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.tpm-x.ant-btn,
.tpm-x.ant-btn:focus,
.tpm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.tpm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.tpm-tools {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px 12px;
}
.tpm-search {
    flex: 1;
    max-width: 280px;

    ::v-deep(.ant-input) {
        height: 36px;
    }
}
.tpm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.tpm-pname {
    font-weight: 600;
    font-size: 13px;
    color: #262626;
}
.tpm-pean {
    font-size: 11px;
    color: #8c8c8c;
}
.tpm-tag {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: #f0f0f0;
    color: #595959;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}
.tpm-tag-winner {
    background: #f6ffed;
    color: #389e0d;
}
.tpm-btn.ant-btn,
.tpm-btn.ant-btn:hover,
.tpm-btn.ant-btn:focus,
.tpm-btn.ant-btn:active {
    height: 28px;
    padding: 0 12px;
    border: 1px solid var(--primary-color);
    background: var(--primary-color-opacity, rgba(232, 96, 44, 0.08));
    color: var(--primary-color);
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    box-shadow: none;
}
.tpm-foot {
    display: flex;
    justify-content: flex-end;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.tpm-btn-ghost.ant-btn,
.tpm-btn-ghost.ant-btn:focus,
.tpm-btn-ghost.ant-btn:active {
    height: 38px;
    padding: 0 18px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #434343;
    font-weight: 500;
    font-size: 14px;
    box-shadow: none;
}
.tpm-btn-ghost.ant-btn:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
}
.tpm-add-product.ant-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;

    .material-icons-outlined {
        font-size: 18px;
    }
}
</style>

<style lang="scss">
.tpm-wrap {
    .ant-modal {
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
